import type { Meta, StoryFn } from '@storybook/react';
import { useMemo, useRef, useState } from 'react';

import {
  FieldReferenceInput,
  FieldReferenceDisplay,
  fieldReferenceMenuHelpers
} from '@pega/cosmos-react-build';
import type {
  FieldReferenceMenuProps,
  FieldReferenceInputProps,
  Field,
  FieldReferenceMenuItem,
  FieldReferenceHandleValue
} from '@pega/cosmos-react-build';
import { Button, Flex } from '@pega/cosmos-react-core';

import { loadingTimeoutMS } from '../../core/Progress/Progress.mocks';

import {
  actions,
  defaultAltContexts,
  fieldPaths,
  getStaticMenuItems,
  link,
  useFieldReferenceMock
} from './FieldReference.mocks';

export default {
  title: 'Build/FieldReference',
  component: FieldReferenceInput
} as Meta;

export interface FieldReferenceStoryProps
  extends Pick<FieldReferenceMenuProps, 'createNewLabel' | 'scrollAt'> {
  label?: FieldReferenceInputProps['label'];
  labelHidden?: FieldReferenceInputProps['labelHidden'];
  info?: FieldReferenceInputProps['info'];
  placeholder?: FieldReferenceInputProps['placeholder'];
  status?: FieldReferenceInputProps['status'];
  required?: FieldReferenceInputProps['required'];
  disabled?: FieldReferenceInputProps['disabled'];
  readOnly?: FieldReferenceInputProps['readOnly'];
  showAdditionalInfo?: boolean;
  showPath?: FieldReferenceInputProps['showPath'];
  hasCreateAction?: boolean;
  showCurrentContext?: FieldReferenceInputProps['showCurrentContext'];
  showActions?: boolean;
  previewable?: boolean;
}

export const FieldReferenceDemo: StoryFn<FieldReferenceStoryProps> = (
  args: FieldReferenceStoryProps
) => {
  const {
    itemsToRender,
    altContexts,
    currentContext,
    parentItem,
    menuLoading,
    handleAltContextClick,
    selected,
    handleSelectedChange,
    filterValue,
    setFilterValue
  } = useFieldReferenceMock('static');

  return (
    <FieldReferenceInput
      additionalInfo={
        args.showAdditionalInfo
          ? {
              content: 'Some additional info'
            }
          : undefined
      }
      label={args.label}
      labelHidden={args.labelHidden}
      info={args.info}
      placeholder={args.placeholder}
      status={args.status}
      required={args.required}
      disabled={args.disabled}
      readOnly={args.readOnly}
      menu={{
        items: itemsToRender,
        altContexts,
        currentContext,
        parentItem,
        loading: menuLoading,
        scrollAt: args.scrollAt,
        onCreateNew: args.hasCreateAction ? () => {} : undefined,
        createNewLabel: args.createNewLabel ? args.createNewLabel : undefined
      }}
      showCurrentContext={args.showCurrentContext}
      onAltContextClick={handleAltContextClick}
      selected={selected}
      onSelectedChange={handleSelectedChange}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      showPath={args.showPath}
      link={args.previewable ? link : undefined}
      actions={args.showActions ? actions : undefined}
    />
  );
};

FieldReferenceDemo.args = {
  label: 'Field Reference',
  labelHidden: false,
  info: 'Type in or click to expand the Popover',
  placeholder: 'Choose a field',
  status: undefined,
  required: false,
  disabled: false,
  readOnly: false,
  showAdditionalInfo: false,
  scrollAt: 7,
  showPath: true,
  showCurrentContext: false,
  hasCreateAction: false,
  createNewLabel: '',
  showActions: false,
  previewable: false
};

FieldReferenceDemo.argTypes = {
  label: { control: { type: 'text' } },
  labelHidden: { control: { type: 'boolean' } },
  info: { control: { type: 'text', label: 'Helper text' } },
  placeholder: { control: { type: 'text' } },
  status: { options: [undefined, 'success', 'warning', 'error'], control: { type: 'select' } },
  required: { control: { type: 'boolean' } },
  disabled: { control: { type: 'boolean' } },
  readOnly: { control: { type: 'boolean' } },
  showAdditionalInfo: { control: { type: 'boolean' } },
  scrollAt: { control: { type: 'number' } },
  showPath: { control: { type: 'boolean' } },
  showCurrentContext: { control: { type: 'boolean' } },
  hasCreateAction: { control: { type: 'boolean' } },
  createNewLabel: { control: { type: 'text' } },
  showActions: { control: { type: 'boolean' } },
  previewable: { control: { type: 'boolean' } }
};

export const FieldReferenceLazyLoadingDemo: StoryFn<FieldReferenceStoryProps> = (
  args: FieldReferenceStoryProps
) => {
  const {
    itemsToRender,
    parentItem,
    menuLoading,
    selected,
    handleSelectedChange,
    filterValue,
    setFilterValue,
    setMenuLoading,
    setItemsShown
  } = useFieldReferenceMock('generated');

  return (
    <FieldReferenceInput
      additionalInfo={
        args.showAdditionalInfo
          ? {
              content: 'Some additional info'
            }
          : undefined
      }
      label={args.label}
      labelHidden={args.labelHidden}
      info={args.info}
      placeholder={args.placeholder}
      status={args.status}
      required={args.required}
      disabled={args.disabled}
      readOnly={args.readOnly}
      menu={{
        items: itemsToRender,
        parentItem,
        loading: menuLoading,
        scrollAt: args.scrollAt,
        loadMore: () => {
          setMenuLoading(true);
          setItemsShown(curr => curr + 25);
          setTimeout(() => {
            setMenuLoading(false);
          }, 500);
        }
      }}
      selected={selected}
      onSelectedChange={handleSelectedChange}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      onMenuOpen={() => setItemsShown(25)}
      link={args.previewable ? link : undefined}
      actions={args.showActions ? actions : undefined}
    />
  );
};

FieldReferenceLazyLoadingDemo.args = {
  label: 'Field Reference',
  labelHidden: false,
  info: 'Type in or click to expand the Popover',
  placeholder: 'Choose a field',
  status: undefined,
  required: false,
  disabled: false,
  readOnly: false,
  showAdditionalInfo: false,
  scrollAt: 7,
  showActions: false,
  previewable: false
};

FieldReferenceLazyLoadingDemo.argTypes = {
  label: { control: { type: 'text' } },
  labelHidden: { control: { type: 'boolean' } },
  info: { control: { type: 'text', label: 'Helper text' } },
  placeholder: { control: { type: 'text' } },
  status: { options: [undefined, 'success', 'warning', 'error'], control: { type: 'select' } },
  required: { control: { type: 'boolean' } },
  disabled: { control: { type: 'boolean' } },
  readOnly: { control: { type: 'boolean' } },
  showAdditionalInfo: { control: { type: 'boolean' } },
  scrollAt: { control: { type: 'number' } },
  showActions: { control: { type: 'boolean' } },
  previewable: { control: { type: 'boolean' } }
};

export const FieldReferenceSelectableContextDemo: StoryFn<FieldReferenceStoryProps> = (
  args: FieldReferenceStoryProps
) => {
  const initialItem: FieldReferenceMenuItem = {
    id: 'primary',
    name: 'Primary',
    type: 'Epic',
    items: []
  };
  const [items, setItems] = useState([initialItem]);
  const [parentItem, setParentItem] = useState<FieldReferenceMenuProps['parentItem']>(undefined);
  const [menuLoading, setMenuLoading] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [selected, setSelected] = useState<Field[]>([]);
  const [altContexts, setAltContexts] = useState<
    FieldReferenceMenuProps['altContexts'] | undefined
  >(defaultAltContexts);
  const selectionTimeoutRef = useRef<number | undefined>();
  const altContextTimeoutRef = useRef<number | undefined>();

  const itemsToRender = useMemo(() => {
    return fieldReferenceMenuHelpers.filterBySearch(items, filterValue);
  }, [filterValue, items]);

  const handleSelectedChange: FieldReferenceInputProps['onSelectedChange'] = selectedValue => {
    window.clearTimeout(selectionTimeoutRef.current);
    window.clearTimeout(altContextTimeoutRef.current);
    setMenuLoading(false);

    if (!selectedValue.length) {
      setSelected([]);
      setItems([initialItem]);
      setParentItem(undefined);
      setAltContexts(defaultAltContexts);
    } else {
      const lastItem = selectedValue.at(-1)!;
      setSelected(selectedValue);

      if (lastItem.items) {
        setAltContexts(undefined);
        setMenuLoading(true);
        setItems([]);
        setParentItem(lastItem);

        // Fake timer to simulate an api request to update the menu items
        selectionTimeoutRef.current = window.setTimeout(() => {
          setMenuLoading(false);
          setItems(getStaticMenuItems(lastItem));
        }, loadingTimeoutMS);
      } else {
        setItems(curr => fieldReferenceMenuHelpers.selectItem(curr, lastItem.id));
      }
    }
  };

  const handleAltContextClick: FieldReferenceInputProps['onAltContextClick'] = altContext => {
    window.clearTimeout(selectionTimeoutRef.current);
    window.clearTimeout(altContextTimeoutRef.current);

    setAltContexts(undefined);
    setMenuLoading(true);
    setItems([]);
    setParentItem(altContext);

    // Fake timer to simulate an api request to update the menu items
    altContextTimeoutRef.current = window.setTimeout(() => {
      setMenuLoading(false);
      setItems(getStaticMenuItems(altContext));
    }, loadingTimeoutMS);
  };

  return (
    <FieldReferenceInput
      additionalInfo={
        args.showAdditionalInfo
          ? {
              content: 'Some additional info'
            }
          : undefined
      }
      label={args.label}
      labelHidden={args.labelHidden}
      info={args.info}
      placeholder={args.placeholder}
      status={args.status}
      required={args.required}
      disabled={args.disabled}
      readOnly={args.readOnly}
      menu={{
        items: itemsToRender,
        altContexts,
        parentItem,
        loading: menuLoading,
        scrollAt: args.scrollAt,
        onCreateNew: args.hasCreateAction ? () => {} : undefined,
        createNewLabel: args.createNewLabel ? args.createNewLabel : undefined
      }}
      showCurrentContext={args.showCurrentContext}
      onAltContextClick={handleAltContextClick}
      selected={selected}
      onSelectedChange={handleSelectedChange}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      onKeyDown={e => {
        if (e.key === '.' && !selected.length) {
          handleSelectedChange([initialItem]);
        }
      }}
      showPath={args.showPath}
      link={args.previewable ? link : undefined}
      actions={args.showActions ? actions : undefined}
    />
  );
};

FieldReferenceSelectableContextDemo.args = {
  label: 'Field Reference',
  labelHidden: false,
  info: 'Type in or click to expand the Popover',
  placeholder: 'Choose a field',
  status: undefined,
  required: false,
  disabled: false,
  readOnly: false,
  showAdditionalInfo: false,
  scrollAt: 7,
  showPath: true,
  showCurrentContext: false,
  hasCreateAction: false,
  createNewLabel: '',
  showActions: false,
  previewable: false
};

FieldReferenceSelectableContextDemo.argTypes = {
  label: { control: { type: 'text' } },
  labelHidden: { control: { type: 'boolean' } },
  info: { control: { type: 'text', label: 'Helper text' } },
  placeholder: { control: { type: 'text' } },
  status: { options: [undefined, 'success', 'warning', 'error'], control: { type: 'select' } },
  required: { control: { type: 'boolean' } },
  disabled: { control: { type: 'boolean' } },
  readOnly: { control: { type: 'boolean' } },
  showAdditionalInfo: { control: { type: 'boolean' } },
  scrollAt: { control: { type: 'number' } },
  showPath: { control: { type: 'boolean' } },
  showCurrentContext: { control: { type: 'boolean' } },
  hasCreateAction: { control: { type: 'boolean' } },
  createNewLabel: { control: { type: 'text' } },
  showActions: { control: { type: 'boolean' } },
  previewable: { control: { type: 'boolean' } }
};

export const FieldReferenceImperativeHandleDemo: StoryFn<FieldReferenceStoryProps> = (
  args: FieldReferenceStoryProps
) => {
  const {
    itemsToRender,
    altContexts,
    currentContext,
    parentItem,
    menuLoading,
    handleAltContextClick,
    selected,
    handleSelectedChange,
    filterValue,
    setFilterValue
  } = useFieldReferenceMock('static');
  const fieldReferenceHandleRef = useRef<FieldReferenceHandleValue>(null);

  return (
    <>
      <Flex container={{ alignItems: 'center', gap: 2 }} item={{ grow: 1 }}>
        <Button
          onClick={() => {
            fieldReferenceHandleRef.current?.openMenu();
          }}
        >
          Open menu
        </Button>
        <Button onClick={() => fieldReferenceHandleRef.current?.closeMenu()}>Close menu</Button>
      </Flex>
      <FieldReferenceInput
        additionalInfo={
          args.showAdditionalInfo
            ? {
                content: 'Some additional info'
              }
            : undefined
        }
        label={args.label}
        labelHidden={args.labelHidden}
        info={args.info}
        placeholder={args.placeholder}
        status={args.status}
        required={args.required}
        disabled={args.disabled}
        readOnly={args.readOnly}
        menu={{
          items: itemsToRender,
          altContexts,
          currentContext,
          parentItem,
          loading: menuLoading,
          scrollAt: args.scrollAt,
          onCreateNew: () => fieldReferenceHandleRef.current?.closeMenu(),
          createNewLabel: 'Create new field'
        }}
        showCurrentContext={args.showCurrentContext}
        onAltContextClick={handleAltContextClick}
        selected={selected}
        onSelectedChange={handleSelectedChange}
        filterValue={filterValue}
        onFilterChange={setFilterValue}
        showPath={args.showPath}
        link={args.previewable ? link : undefined}
        actions={args.showActions ? actions : undefined}
        handle={fieldReferenceHandleRef}
      />
    </>
  );
};

FieldReferenceImperativeHandleDemo.args = {
  label: 'Field Reference',
  labelHidden: false,
  info: 'Type in or click to expand the Popover',
  placeholder: 'Choose a field',
  status: undefined,
  required: false,
  disabled: false,
  readOnly: false,
  showAdditionalInfo: false,
  scrollAt: 7,
  showPath: true,
  showCurrentContext: false,
  showActions: false,
  previewable: false
};

FieldReferenceImperativeHandleDemo.argTypes = {
  label: { control: { type: 'text' } },
  labelHidden: { control: { type: 'boolean' } },
  info: { control: { type: 'text', label: 'Helper text' } },
  placeholder: { control: { type: 'text' } },
  status: { options: [undefined, 'success', 'warning', 'error'], control: { type: 'select' } },
  required: { control: { type: 'boolean' } },
  disabled: { control: { type: 'boolean' } },
  readOnly: { control: { type: 'boolean' } },
  showAdditionalInfo: { control: { type: 'boolean' } },
  scrollAt: { control: { type: 'number' } },
  showPath: { control: { type: 'boolean' } },
  showCurrentContext: { control: { type: 'boolean' } },
  showActions: { control: { type: 'boolean' } },
  previewable: { control: { type: 'boolean' } }
};

export interface FieldReferenceDisplayDemoProps {
  type: string;
  showActions?: boolean;
  previewable?: boolean;
  isNested?: boolean;
}

export const FieldReferenceDisplayDemo: StoryFn<FieldReferenceDisplayDemoProps> = (
  args: FieldReferenceDisplayDemoProps
) => {
  let fieldPath = fieldPaths[args.type];
  if (args.isNested && args.type !== 'field reference') {
    fieldPath = [
      {
        id: 'userStory',
        name: 'User story',
        type: 'User story (reference)',
        icon: { set: 'streamline', name: 'case' }
      },
      {
        id: 'upstreamDepBugs',
        name: 'Upstream dependency bugs',
        type: 'Upstream dependency (reference)',
        icon: { set: 'streamline', name: 'case' }
      },
      ...fieldPath
    ];
  }

  return (
    <FieldReferenceDisplay
      fieldPath={fieldPath}
      link={args.previewable ? link : undefined}
      actions={args.showActions ? actions : undefined}
    />
  );
};

FieldReferenceDisplayDemo.args = {
  type: 'Text',
  showActions: false,
  previewable: false,
  isNested: true
};

FieldReferenceDisplayDemo.argTypes = {
  type: {
    options: [
      'Text',
      'Paragraph',
      'Boolean',
      'Date',
      'Time',
      'Date & time',
      'Integer',
      'Decimal',
      'Currency',
      'Email',
      'Phone',
      'Percentage',
      'Picklist',
      'URL',
      'Location',
      'Attachment',
      'User reference',
      'Embedded field',
      'Field reference'
    ],
    control: { type: 'select' }
  },
  showActions: { control: { type: 'boolean' } },
  previewable: { control: { type: 'boolean' } },
  isNested: { control: { type: 'boolean' } }
};
